import React, { useEffect, useState } from 'react';
import { i18nFn, useAuthPin, useRequestPin, useSpringBigStore } from '@springbig/shared';
import { VStack, HStack, Pressable, FormControl, Input, WarningIcon } from 'native-base';
import { Caption, H3 } from '~/shared/Typography';
import Button from '~/shared/Button';
import { useFormStyles, useGeneralStyles } from '~/shared/styles';
import * as yup from 'yup';
import { useController, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppNavigate } from '~/pages';

type LoginStepProps = {
    t: i18nFn;
    onEvent: (message: string) => void;
};

export const LoginStep = ({
    t,
    onEvent,
}: LoginStepProps) => {

    const [disabled, setDisabled] = useState(false);
    const { params } = useAppNavigate();
    const gStyle = useGeneralStyles();
    const {
        input_background,
        input_border,
        input_label,
        input_text
    } = useFormStyles();

    useEffect(() => {
        let timer;

        if (disabled) {
            timer = setTimeout(() => {
                setDisabled(false);
            }, 60 * 1000);

            return () => clearTimeout(timer);
        }
    }, [disabled]);

    const codeSize = 4;

    const schemaCode = yup.object().shape({
        code: yup
            .string()
            .required('PIN is required')
            .matches(new RegExp(`^.{${codeSize}}$`), 'invalid PIN format')
            .trim(),
    });

    type FormDataCode = yup.InferType<typeof schemaCode>;

    const { control: controlCode, handleSubmit: handleSubmitCode, formState: { errors: errorsCode } } = useForm({
        resolver: yupResolver(schemaCode)
    });

    const { field: codeField } = useController({
        control: controlCode,
        defaultValue: '',
        name: 'code',
    });

    const { mutate: authPin, isError, error, isLoading } = useAuthPin();

    const onSubmitCode = (data: FormDataCode): void => {

        authPin({
            pin: btoa(data.code),
            stashboardOrMemberKey: params.wallet_id,
            reference: 'member'
        }, {
            onSuccess: (_) => {

                onEvent('welcome');
            }
        });
    };

    const { mutate: requestPin } = useRequestPin();

    const handleResendCode = () => {

        requestPin({
            stashboardOrMemberKey: params.wallet_id,
            reference: 'member',
            pin_length: 4,
        });
        setDisabled(true);
    };

    return (
        <VStack space={8} w={'100%'} alignItems={'center'}>
            <VStack w={'100%'}>
                <H3 fontSize={20}>
                    { t('auth.ten_dlc.login.title') }
                </H3>
                <Caption maxW={'100%'}>
                    { t('auth.ten_dlc.login.description') }
                </Caption>
            </VStack>
            <VStack space={4} w={'100%'}>
                <FormControl isInvalid={!!errorsCode.code} w={'100%'}>
                    <FormControl.Label color={input_label}>
                        { t('auth.ten_dlc.login.enter_pin') }
                    </FormControl.Label>
                    <Input
                        {...codeField}
                        backgroundColor={input_background}
                        borderColor={input_border}
                        height={10}
                        fontSize={16}
                        color={input_text}
                        isInvalid={!!errorsCode.code}
                        isDisabled={isLoading}
                        maxLength={codeSize}
                        returnKeyType='done'
                    />
                    { errorsCode && errorsCode.code &&
                        <FormControl.ErrorMessage
                            leftIcon={<WarningIcon size={3} />}
                        >
                            { errorsCode.code?.message }
                        </FormControl.ErrorMessage>
                    }
                    { isError && error &&
                        <HStack alignItems={'center'} space={2}>
                            <WarningIcon size={3} color={gStyle.button_danger_background} />
                            <Caption color={gStyle.button_danger_background} fontSize={12}>
                                { (error.data.error) as any}
                            </Caption>
                        </HStack>
                    }
                </FormControl>
                <Button
                    bg={gStyle.button_background}
                    _text={{ color: gStyle.button_text }}
                    onPress={handleSubmitCode(onSubmitCode)}
                    isLoading={isLoading}
                >
                    { t('auth.ten_dlc.login.submit_pin') }
                </Button>
                <Caption textAlign={'center'} mb={-2}>
                    { t('auth.ten_dlc.login.forgot_pin') }
                </Caption>
                <Button
                    bg={gStyle.button_neutral_background}
                    opacity={disabled ? .5 : 1}
                    _text={{ color: gStyle.button_neutral_text }}
                    onPress={handleResendCode}
                    isLoading={isLoading}
                >
                    { t('auth.ten_dlc.login.send_pin') }
                </Button>
            </VStack>
        </VStack>
    );
};
